package dev.lingvo.site.pages.app.account

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.shared.NotificationEvent
import dev.lingvo.shared.Role
import dev.lingvo.shared.atLeast
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCheckbox
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.pages.app.projects.slug.settings.settingsOperationResult
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * What Lingvo emails you about.
 *
 * **Security email is not on this page, and is not a disabled row on it either.** An attacker who can
 * silence the alert about their own intrusion has been handed the last piece; a greyed-out toggle
 * would tell them the setting exists and invite them to find another way to it.
 *
 * The workspace rows are **absent** for translators and viewers rather than disabled, for the same
 * reason: a control somebody cannot use is an invitation to ask why, and the honest answer is that
 * those events are not about their work.
 */
@Page("/app/account/notifications")
@Layout(".components.layouts.AppLayout")
@Composable
fun NotificationsPage() {
    val scope = rememberCoroutineScope()
    var refreshKey by remember { mutableStateOf(0) }
    // **The switch being saved, not "a switch is being saved".** One page-level flag disabled all
    // eight checkboxes for the length of one round trip, so a reader turning three of them off
    // flipped the first and had the next two clicks land on dead controls and vanish.
    var saving by remember { mutableStateOf<NotificationEvent?>(null) }
    var message by remember { mutableStateOf<String?>(null) }
    // Every toggle bumps this counter to re-read the settings. As a key it collapsed the whole
    // panel to skeleton bars and unmounted the checkbox that had just been clicked, so the control
    // the reader was using disappeared under them on every change.
    // The whole `Reloadable`, not just `.state`. A refresh that fails over an already-visible
    // success is deliberately kept out of `state` — that is how the value stays on screen — and
    // lands in `refreshError` instead. Taking `.state` and discarding the rest meant a toggle that
    // saved, followed by a re-read that failed, silently restored the pre-toggle checkbox with no
    // message at all: the reader sees their change undone and is told nothing.
    val settings = loadReloadable(Session.requestIdentity, reloadToken = refreshKey) {
        ApiClient.notificationSettings()
    }
    val state = settings.state

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(t("app.account.notifications.notifications", "Notifications"), t("app.account.notifications.what_reaches_your_inbox_and_what", "What reaches your inbox, and what stays in the app."))

        // `live`: this appears only because an action the reader just took failed, and it appears
        // above the control they are still on. A warning that arrives silently is one a
        // keyboard reader never learns about, and they will press the button again.
        (message ?: settings.refreshError?.message)?.let {
            LAlert(
                t("app.account.notifications.that_didnt_work", "That didn't work"),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }

        LAsyncContent(
            state = state,
            loadingLabel = t("app.account.notifications.loading_your_settings", "Loading your settings"),
            subject = t("app.account.notifications.your_notification_settings", "your notification settings"),
            onRetry = { refreshKey++ },
            // Three siblings, so they need the page column's own spacing back: wrapped, they are
            // one child of it rather than three.
            gap = Space.XL,
        ) { loaded ->
            val settings = loaded?.settings.orEmpty().associateBy { it.event }
            val role = Session.strongestRole()
            val manages = role != null && role.atLeast(Role.ADMIN)

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    LSectionTitle(t("app.account.notifications.your_work", "Your work"))
                    NotificationEvent.entries.filter { !it.workspaceLevel }.forEach { event ->
                        Toggle(event, settings[event]?.enabled ?: true, saving == event) { enabled ->
                            scope.launch {
                                saving = event
                                // `settingsOperationResult`, not `runCatching`: the call suspends,
                                // and `runCatching` catches the `CancellationException` a
                                // navigation throws through this scope like any other error — so
                                // flipping a switch and leaving announced "That didn't work" in a
                                // live region for an operation that never failed.
                                message = settingsOperationResult {
                                    ApiClient.updateNotification(event, enabled)
                                }.fold({ refreshKey++; null }, { it.message })
                                saving = null
                            }
                        }
                    }
                }
            }

            if (manages) {
                LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                        LSectionTitle(t("app.account.notifications.the_workspace", "The workspace"))
                        NotificationEvent.entries.filter { it.workspaceLevel }.forEach { event ->
                            Toggle(event, settings[event]?.enabled ?: true, saving == event) { enabled ->
                                scope.launch {
                                    saving = event
                                    message = settingsOperationResult {
                                        ApiClient.updateNotification(event, enabled)
                                    }.fold({ refreshKey++; null }, { it.message })
                                    saving = null
                                }
                            }
                        }
                    }
                }
            }

            // Stated plainly rather than implied by an absence, so nobody goes looking for a
            // switch that deliberately does not exist.
            LMuted(
                t("app.account.notifications.security_email_a_new_sign_in_2", "Security email — a new sign-in, a password change, an address change — is always " +
                    "sent and cannot be turned off. It is the one message that has to arrive on " +
                    "the day somebody else is in your account."),
            )
            Link(path = "/app/account") { Text(t("app.account.notifications.back_to_your_account", "Back to your account")) }
        }
    }
}

@Composable
private fun Toggle(event: NotificationEvent, enabled: Boolean, busy: Boolean, onChange: (Boolean) -> Unit) {
    LCheckbox(
        checked = enabled,
        onCheckedChange = onChange,
        label = event.description,
        enabled = !busy,
    )
}

